import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  memo,
  ReactNode,
} from "react";
import { Button } from "../ui/Button";
import { trpc } from "~/TrpcClient";
import css from "./Comments.module.css";
import { Heading3 } from "../ui/Heading";
import { Link, useLocation } from "react-router";
import { formatDate } from "./Date";
import clsx from "clsx";
import { pg } from "~/pg.server";
import { and, eq, sql } from "drizzle-orm";
import { OurLoaderArgs } from "~/util/Loaders";
import { GameVer, Realm, tt, tturl, useRealm, useT } from "~/util/Lang";
import { GameData, select } from "gamedata/GameData";
import { loadMarkdown } from "~/util/Markdown.server";
import { Root } from "node_modules/remark-parse/lib";
import { MDDat, renderMarkdown } from "~/util/MarkdownRenderer";
import { UserLink } from "./UserLink";
import { ReplyIcon } from "./icons/Reply";
import { PencilIcon } from "./icons/Pencil";
import { getQuestPath, QuestLink, QuestLinkData } from "../quest/QuestLink";
import { getItemPath, ItemLink, ItemLinkData } from "../item/ItemLink";
import { getNpcPath, NpcLink, NpcLinkData } from "../npc/NpcLink";
import { getSkillPath, SkillLink, SkillLinkData } from "../skill/SkillLink";
import { npc } from "drizzle/gd";

export type CommentObjectType = "item" | "npc" | "quest" | "skill";

type MarkDown = {
  root: Root;
  dat: MDDat;
};

export type CommentData = {
  id: number;
  createdAt: string;
  parentId?: number | null;
  user: { id: number; name: string; avatar?: string | null; isAdmin?: boolean };
  contentMarkdown: MarkDown;
  content: string;
  rating: number;
};

export type SubjectData = {
  objType: CommentObjectType;
  objUname: string;
};

export type AuthData =
  | {
      id: number;
      login: string;
      isAdmin?: boolean | null;
      avatar?: string | null;
    }
  | undefined;

export function Comments({
  comments: initialComments,
  authUser,
  subject,
}: {
  comments: CommentData[];
  authUser: AuthData;
  subject: SubjectData;
}) {
  const [comments, setComments] = useState<CommentData[]>(initialComments);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const commentRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const t = useT();
  const location = useLocation();
  const isAdmin = authUser?.isAdmin ?? false;

  // Группировка комментариев по parentId
  const groupedComments = useMemo(() => {
    const groups = new Map<number | null, CommentData[]>();
    comments.forEach((comment) => {
      const key = comment.parentId ?? null;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)?.push(comment);
    });
    return groups;
  }, [comments]);

  const deleteComment = trpc.comments.deleteComment.useMutation();

  async function handleDeleteComment(commentId: number) {
    const isConfirmed = window.confirm(
      t({
        en: "Are you sure you want to delete this comment?",
        ru: "Вы уверены, что хотите удалить этот комментарий?",
      })
    );

    if (isConfirmed) {
      try {
        const result = await deleteComment.mutateAsync({ commentId });
        if (result.t === "success") {
          setComments((prevComments) =>
            prevComments.filter((comment) => comment.id !== commentId)
          );
        }
      } catch (error) {
        console.error("Error when deleting a comment:", error);
      }
    }
  }

  const handleCreateComment = useCallback((newComment: CommentData) => {
    setComments((prev) => {
      if (newComment.parentId) {
        const children = prev.filter((c) => c.parentId === newComment.parentId);

        if (children.length > 0) {
          const lastChildIndex = prev.findIndex(
            (c) => c.id === children[children.length - 1].id
          );
          const newArray = [...prev];
          newArray.splice(lastChildIndex + 1, 0, newComment);
          return newArray;
        }

        const parentIndex = prev.findIndex((c) => c.id === newComment.parentId);
        if (parentIndex > -1) {
          const newArray = [...prev];
          newArray.splice(parentIndex + 1, 0, newComment);
          return newArray;
        }
      }

      return [...prev, newComment];
    });

    setTimeout(() => {
      const element = commentRefs.current.get(newComment.id);
      element?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 100);
  }, []);

  const handleSaveComment = useCallback(
    (commentId: number, newContent: string, newContentMarkdown: MarkDown) => {
      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                content: newContent,
                contentMarkdown: newContentMarkdown,
              }
            : comment
        )
      );
      setEditingCommentId(null);
    },
    []
  );

  const updateVote = trpc.comments.voteComment.useMutation();
  const handleVote = useCallback(async (commentId: number, rating: number) => {
    try {
      const result = await updateVote.mutateAsync({ commentId, rating });
      if (result.t === "success") {
        setComments((prev) =>
          prev.map((c) =>
            c.id === commentId ? { ...c, rating: result.data.rating } : c
          )
        );
      }
    } catch (error) {
      console.error("Voting error:", error);
    }
  }, []);

  const renderComment = (comment: CommentData) => (
    <CommentItem
      key={comment.id}
      comment={comment}
      authUser={authUser}
      isAdmin={isAdmin}
      refMap={commentRefs}
      editingCommentId={editingCommentId}
      onEditStart={setEditingCommentId}
      onEditSave={handleSaveComment}
      onDelete={handleDeleteComment}
      onReply={setReplyingTo}
      onVote={handleVote}
    />
  );

  const renderCommentGroup = (comment: CommentData) => {
    const replies = comments.filter((c) => c.parentId === comment.id);

    return (
      <div key={comment.id} className={css.commentGroup}>
        {renderComment(comment)}

        {replies.map((reply) => (
          <div key={reply.id} className={css.reply}>
            {renderComment(reply)}
          </div>
        ))}

        {replyingTo === comment.id && (
          <div className={css.commentItem}>
            <NewComment
              subject={subject}
              authUser={authUser}
              parentId={comment.id}
              onCommentCreated={handleCreateComment}
              onCancel={() => setReplyingTo(null)}
              buttonText={t({ en: "Reply", ru: "Ответить" })}
            />
          </div>
        )}
        <div className={css.commentGroupFooter}>
          {!comment.parentId && !replyingTo && (
            <Button onClick={() => setReplyingTo(comment.id)}>
              <ReplyIcon width={18} height={18} />
              {t({ en: "Reply", ru: "Ответить" })}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={css.commentList}>
        {comments.filter((c) => !c.parentId).map(renderCommentGroup)}
      </div>

      <div className="content">
        <NewComment
          subject={subject}
          authUser={authUser}
          onCommentCreated={handleCreateComment}
          buttonText={t({ en: "Add Comment", ru: "Ответить" })}
          title={t({ en: "Post a Comment", ru: "Добавить комментарий" })}
        />
      </div>
    </>
  );
}
const CommentItem = memo(
  ({
    comment,
    authUser,
    isAdmin,
    refMap,
    editingCommentId,
    onEditStart,
    onEditSave,
    onDelete,
    onReply,
    onVote,
  }: {
    comment: CommentData;
    authUser: AuthData;
    isAdmin: boolean;
    refMap: React.MutableRefObject<Map<number, HTMLDivElement>>;
    editingCommentId: number | null;
    onEditStart: (id: number | null) => void;
    onEditSave: (id: number, content: string, md: MarkDown) => void;
    onDelete: (id: number) => void;
    onReply: (id: number) => void;
    onVote: (id: number, rating: number) => void;
  }) => {
    const isAuthor = authUser?.id === comment.user.id;
    const canVote = authUser && !isAuthor;
    const t = useT();
    const realm = useRealm();

    return (
      <div
        className={css.commentItem}
        id={`comment-${comment.id}`}
        ref={(el) => el && refMap.current.set(comment.id, el)}
      >
        <VoteButtons
          commentId={comment.id}
          rating={comment.rating}
          onVote={onVote}
          disabledButtons={!canVote}
        />

        <div className={css.commentContent}>
          <div className={css.header}>
            <UserLink user={comment.user} />
            <span className={css.date}>on {formatDate(comment.createdAt)}</span>
            <div className={css.control}>
              {isAuthor && (
                <button
                  className="link"
                  onClick={() => onEditStart(comment.id)}
                >
                  {t({ en: "Edit", ru: "Редактировать" })}
                </button>
              )}

              {isAdmin && (
                <button
                  className="link"
                  style={{ color: "red" }}
                  onClick={() => onDelete(comment.id)}
                >
                  {t({ en: "Delete", ru: "Удалить" })}
                </button>
              )}
            </div>
          </div>

          {editingCommentId === comment.id ? (
            <EditComment
              comment={comment}
              onSave={onEditSave}
              onCancel={() => onEditStart(null)}
            />
          ) : (
            <>
              <div className={css.content}>
                {renderMarkdown({
                  ...comment.contentMarkdown,
                  gver: realm.game,
                })}
              </div>

              <div className={css.footer}></div>
            </>
          )}
        </div>
      </div>
    );
  }
);

function VoteButtons({
  commentId,
  rating,
  onVote,
  disabledButtons,
}: {
  commentId: number;
  rating: number;
  onVote: (id: number, value: number) => void;
  disabledButtons: boolean;
}) {
  return (
    <div className={css.rating}>
      <div
        className={clsx(css.button, css.inc, {
          [css.disabled]: disabledButtons,
        })}
        onClick={() => onVote(commentId, 1)}
      >
        <div className={clsx(css.arrow, css.up)}></div>
      </div>
      <div className={css.count}>{rating}</div>
      <div
        className={clsx(css.button, css.dec, {
          [css.disabled]: disabledButtons,
        })}
        onClick={() => onVote(commentId, -1)}
      >
        <div className={clsx(css.arrow, css.down)}></div>
      </div>
    </div>
  );
}

function NewComment({
  subject,
  authUser,
  onCommentCreated,
  parentId,
  onCancel,
  title,
  buttonText,
}: {
  subject: SubjectData;
  authUser: AuthData;
  onCommentCreated: (newComment: CommentData) => void;
  parentId?: number | null;
  onCancel?: () => void;
  title?: string;
  buttonText: string;
}) {
  const loc = useLocation();
  const realm = useRealm();

  const [text, setText] = useState("");
  const createComment = trpc.comments.createComment.useMutation();

  async function doCreate() {
    const result = await createComment.mutateAsync({
      content: text,
      subject,
      lang: realm.lang,
      game: realm.game,
      parentId,
    });
    if (result.t === "success") {
      const newComment = {
        id: result.data.commentId,
        createdAt: result.data.createdAt,
        user: {
          id: authUser!.id,
          name: authUser!.login,
          avatar: authUser!.avatar,
        },
        content: text,
        contentMarkdown: result.data.contentMarkdown,
        rating: 0,
        lang: realm.lang,
        game: realm.game,
        parentId,
      };
      onCommentCreated(newComment);
      setText("");
    }
  }

  return (
    <div className={css.newComment}>
      {title && <Heading3>{title}</Heading3>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          doCreate();
        }}
      >
        <textarea
          className={css.input}
          disabled={!authUser}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {authUser ? (
          <Button type="submit" disabled={!text}>
            <PencilIcon width={18} height={18} />
            {buttonText}
          </Button>
        ) : (
          <div className={css.desc}>
            You are not logged in. Please{" "}
            <Link to={`/login?r=${loc.pathname}`}>log in</Link> or{" "}
            <Link to={`/register?r=${loc.pathname}`}>register</Link> an account
            to add your comment.
          </div>
        )}
      </form>
    </div>
  );
}

function EditComment({
  comment,
  onSave,
  onCancel,
}: {
  comment: CommentData;
  onSave: (
    commentId: number,
    newContent: string,
    newContentMarkdown: MarkDown
  ) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(comment.content);
  const updateComment = trpc.comments.updateComment.useMutation();
  const t = useT();

  async function doUpdate() {
    const result = await updateComment.mutateAsync({
      content: text,
      commentId: comment.id,
    });
    if (result.t === "success") {
      onSave(comment.id, text, result.data.contentMarkdown);
    }
  }

  return (
    <div className={css.newComment} style={{ marginTop: 10 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          doUpdate();
        }}
      >
        <textarea
          className={css.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div style={{ display: "inline-flex", gap: 10 }}>
          <Button type="submit" disabled={!text}>
            {t({ en: "Save comment", ru: "Сохранить" })}
          </Button>
          <Button type="button" onClick={onCancel}>
            {t({ en: "Cancel", ru: "Отменить" })}
          </Button>
        </div>
      </form>
    </div>
  );
}

export async function getComments({
  id,
  type,
  args,
}: {
  id: string;
  type: CommentObjectType;
  args: { realm: Realm; gd: GameData };
}) {
  const comments = await pg.db
    .select({
      id: pg.comments.id,
      content: pg.comments.content,
      user: { id: pg.users.id, name: pg.users.login, avatar: pg.users.avatar },
      createdAt: pg.comments.createdAt,
      rating: sql<number>`COALESCE(SUM(${pg.commentVotes.rating}), 0) AS rating`,
      game: pg.comments.game,
      parentId: pg.comments.parentId,
    })
    .from(pg.comments)
    .where(
      and(
        eq(pg.comments.objType, type),
        eq(pg.comments.objUname, id),
        eq(pg.comments.isDeleted, false)
      )
    )
    .innerJoin(pg.users, eq(pg.comments.userId, pg.users.id))
    .leftJoin(pg.commentVotes, eq(pg.comments.id, pg.commentVotes.commentId))
    .groupBy(pg.comments.id, pg.users.id)
    .orderBy(sql`created_at ASC`);

  const commentsWithMarkdown = await Promise.all(
    comments.map(async (comment) => {
      const gver = comment.game as GameVer;

      const contentWithMarkdown = await loadMarkdown(
        comment.content,
        args.gd.all_gd!,
        args.realm.lang,
        gver
      );

      return {
        ...comment,
        contentMarkdown: contentWithMarkdown,
      };
    })
  );

  return commentsWithMarkdown;
}

export const getCommentPreview = (text: string, maxWords = 20) => {
  const words = text.split(" ");
  if (words.length <= maxWords) return words.join(" ");
  return `${words.slice(0, maxWords).join(" ")}...`;
};

export type LastComment = {
  id: number;
  content: string;
  objType: string;
  objUname?: string | null;
  createdAt: string;
  page: number;
  lang: string;
  game: string;
};

export type LastCommentWithObject =
  | (LastComment & { _: "quest"; linkData: QuestLinkData })
  | (LastComment & { _: "item"; linkData: ItemLinkData })
  | (LastComment & { _: "npc"; linkData: NpcLinkData })
  | (LastComment & { _: "skill"; linkData: SkillLinkData });

export function commentsWithObjects(props: {
  comments: LastComment[];
  args: OurLoaderArgs;
}): LastCommentWithObject[] {
  const { comments, args } = props;
  const { realm } = args;
  const t = tt.bind(null, realm.lang);

  return comments
    .map((comment) => {
      const gver = comment.game as GameVer;
      if (comment.objType === "quest") {
        if (!comment.objUname) {
          return null;
        }

        const quest = args.gd.all_gd?.[gver].quests.get(
          parseInt(comment.objUname)
        );
        if (quest) {
          const data = select(quest, { id: true, uname: true, name: t });
          return {
            _: "quest" as const,
            linkData: {
              id: data.id,
              uname: quest.uname,
              name: data.name,
              gver,
            },
            ...comment,
          };
        } else {
          return null;
        }
      }
      if (comment.objType === "npc") {
        if (!comment.objUname) {
          return null;
        }

        const npc = args.gd.all_gd?.[gver].npcs.get(comment.objUname!);
        if (npc) {
          const data = select(npc, {
            npcName: true,
            id: true,
            level: true,
            nick: t,
            nickColor: true,
            name: t,
            race: { icon: true },
          });
          return {
            _: "npc" as const,
            linkData: {
              npcName: data.npcName,
              name: data.name,
              level: data.level,
              nick: data.nick,
              nickColor: data.nickColor,
              race: data.race,
              gver,
            },
            ...comment,
          };
        } else {
          return null;
        }
      }
      if (comment.objType === "item") {
        if (!comment.objUname) {
          return null;
        }
        const item = args.gd.all_gd?.[gver].items.get(comment.objUname!);

        if (item) {
          const data = select(item, {
            itemName: true,
            addName: t,
            id: true,
            name: t,
            icon: true,
            crystalType: true,
          });
          return {
            _: "item" as const,
            linkData: {
              itemName: data.itemName,
              name: data.name,
              addName: data.addName,
              icon: data.icon,
              crystalType: data.crystalType,
              gver,
            },
            ...comment,
          };
        } else {
          return null;
        }
      }
      if (comment.objType === "skill") {
        if (!comment.objUname) {
          return null;
        }

        const skill = args.gd.all_gd?.[gver].skills.get(
          parseInt(comment.objUname)
        );
        if (skill) {
          const data = select(skill, {
            cardName: true,
            skillName: true,
            id: true,
            name: t,
            icon: true,
          });
          return {
            _: "skill" as const,
            linkData: {
              id: data.id,
              cardName: data.cardName,
              level: 1,
              name: data.name,
              icon: data.icon,
            },
            ...comment,
          };
        }
        return null;
      } else {
        return null;
      }
    })
    .filter((comment) => comment !== null);
}

type CommentObject =
  | { _: "quest"; linkData: QuestLinkData }
  | { _: "item"; linkData: ItemLinkData }
  | { _: "npc"; linkData: NpcLinkData }
  | { _: "skill"; linkData: SkillLinkData };

export function getCommentObject(comment: CommentObject) {
  let link: ReactNode = null;
  let path: string = "/";

  switch (comment._) {
    case "quest":
      link = <QuestLink quest={comment.linkData} isLink={false} />;
      path = getQuestPath({ quest: comment.linkData });
      break;
    case "npc":
      link = (
        <NpcLink
          npc={comment.linkData}
          showIcon={true}
          showLvl={false}
          isLink={false}
        />
      );
      path = getNpcPath({ npc: comment.linkData });
      break;
    case "item":
      link = <ItemLink item={comment.linkData} isLink={false} />;
      path = getItemPath({ item: comment.linkData });
      break;
    case "skill":
      link = <SkillLink data={comment.linkData} isLink={false} />;
      path = getSkillPath({ skill: comment.linkData });
      break;
  }

  return { link, path };
}
